import {
  FacebookLoginProvider,
  GoogleLoginProvider,
  MicrosoftLoginProvider,
  SocialUser
} from '@abacritt/angularx-social-login';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ScriptsService } from '@roller/rdl/utilities/scripts';
import { BehaviorSubject, Observable, catchError, finalize, of } from 'rxjs';
import { AnalyticsService } from '../analytics/analytics.service';
import { InitialConfigService } from '../app-config/initial-config.service';
import { CStorageKeys } from '../storage/storage-keys.const';
import { StorageService } from '../storage/storage.service';
import { IAppleAuthResponse } from './apple-login/apple-login.model';
import { AppleLoginProvider } from './apple-login/apple-login.provider';
import {
  EAuthEndpoints,
  ECheckoutUserIdentityProvider,
  ECheckoutUserLoginType,
  IGuestAuthJwt,
  IGuestAuthPayload,
  IGuestAuthVerifyCodeDTO,
  IKnownGuestData
} from './auth.model';
import { HttpResponseHelperService } from '../http-response/http-response-helper.service';
import { ICountryPhone } from '@roller/rdl/design/phone-country-input';
import { AccountService } from '@core/account/account.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly storageKeys = CStorageKeys;
  private readonly appleAuthScriptSrc =
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

  private authResult: IGuestAuthJwt | undefined;
  private isLoggedIn$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isAppleSignInLoaded = false;

  constructor(
    private http: HttpClient,
    private initialConfigService: InitialConfigService,
    private storageService: StorageService,
    private scriptsService: ScriptsService,
    private analyticsService: AnalyticsService,
    private httpResponseHelperService: HttpResponseHelperService,
    private accountService: AccountService
  ) {}

  /**
   * Get's the current guest account logged in state as an observable.
   * @returns Observable of guests logged in state
   */
  getIsLoggedIn(): Observable<boolean> {
    return this.isLoggedIn$.asObservable();
  }

  /** Sets the current logged in state in the shared observable */
  setIsLoggedIn(value: boolean): void {
    this.isLoggedIn$.next(value);
    this.analyticsService.setLoggedInStatus(value);
  }

  isAuthenticated(): boolean {
    return !!this.authResult;
  }

  /**
   * Loads auth from storage and checks it's expiry to ensure it's valid
   * Use time value to determine if the expiry is before the current time
   * *NOTE: this should likely be checked by the interceptor if refresh tokens are going to be implemented
   * @returns valid auth state
   */
  isStoredAuthValid(): boolean {
    const storageAuth = this.getAuthCredentials();

    if (!storageAuth) {
      return false;
    }

    const now = new Date();
    const expiry = new Date(storageAuth.expiry);

    if (!expiry || expiry.getTime() - now.getTime() < 0) {
      this.logout();
      return false;
    }

    return true;
  }

  /**
   * Fetches stored auth credentials from storage
   * @returns auth credentials it as a interface or undefined
   */
  getAuthCredentials(): IGuestAuthJwt | undefined {
    if (this.authResult) {
      return this.authResult;
    }

    const storageAuth = this.storageService.getItem<IGuestAuthJwt>(this.storageKeys.auth, true);

    if (!storageAuth || typeof storageAuth === 'string') {
      return undefined;
    }

    return storageAuth;
  }

  /**
   * Stores guest auth credentials in local storage
   * @param auth guest account auth object with token and expiry
   */
  setAuthCredentials(auth: IGuestAuthJwt, saveInStorage = true): void {
    this.authResult = auth;

    if (saveInStorage) {
      const authString = JSON.stringify(auth);
      this.storageService.setItem(this.storageKeys.auth, authString);
    }
  }

  /**
   * Calls the API to verify a login OTP code and receive a new auth JWT
   * @param code login specific OTP verification code
   * @returns http request observable - 200 Auth JWT or 409 error
   */
  verifyCode(payload: IGuestAuthVerifyCodeDTO): Observable<IGuestAuthJwt> {
    return this.http.post<IGuestAuthJwt>(
      `${this.initialConfigService.getBaseApiUrl()}/checkout/user/verify-code`,
      payload
    );
  }

  /**
   * Calls the API to verify OTP code. Sends type of account details change and new value
   * @param payload params - verification code, type - phone/email, updated value
   * @returns http request observable - 200 Auth JWT or 409 error
   */
  verifyAccountDetails(payload: IGuestAuthVerifyCodeDTO): Observable<IGuestAuthJwt> {
    return this.http.post<IGuestAuthJwt>(
      `${this.initialConfigService.getBaseApiUrl()}/checkout/user/details/verify-update`,
      payload
    );
  }

  /**
   * Calls the API to log in the current guest in by requesting a verification code to be sent
   * @param payload params for type and uuid
   * @returns empty or user details when verification is not required (social auth)
   */
  login(payload: IGuestAuthPayload): Observable<undefined | IGuestAuthJwt> {
    return this.http.post<undefined | IGuestAuthJwt>(
      `${this.initialConfigService.getBaseApiUrl()}/checkout/user/login`,
      payload
    );
  }

  /**
   * Calls the API to sign up the current guest by requesting a verification code to be sent
   * @param payload params for type and uuid
   * @returns empty or unique identifier string when verification is not required (social auth)
   */
  signUp(payload: IGuestAuthPayload): Observable<IGuestAuthJwt | undefined> {
    return this.http.post<IGuestAuthJwt | undefined>(
      `${this.initialConfigService.getBaseApiUrl()}/checkout/user/sign-up`,
      payload
    );
  }

  /**
   * Calls the API to sign out the current guest. Clears stored auth.
   * @returns http request observable - 200 empty success or 409 error
   */
  signOut(): Observable<undefined> {
    return this.http.post<undefined>(`${this.initialConfigService.getBaseApiUrl()}${EAuthEndpoints.logout}`, null).pipe(
      catchError(() => of(undefined)),
      finalize(() => this.logout())
    );
  }

  /** Get mobile number and email for existing guest by customer unique id. */
  getEmailMobileByCustomerId(customerId: string): Observable<IKnownGuestData | undefined> {
    return this.http
      .get<IKnownGuestData>(`${this.initialConfigService.getBaseApiUrl()}/checkout/user/${customerId}`)
      .pipe(catchError(() => of(undefined)));
  }

  /**
   * Calls the API to verify a login OTP code and receive a unique GUID
   * @param code login specific OTP verification code
   * @returns http request observable - unique identifier string
   */
  verifySignUpCode(payload: IGuestAuthVerifyCodeDTO): Observable<IGuestAuthJwt> {
    return this.http.post<IGuestAuthJwt>(
      `${this.initialConfigService.getBaseApiUrl()}/checkout/user/verify-sign-up/v2`,
      payload
    );
  }

  /** Clears storage auth objects and sets the app logged in state to false */
  logout(): void {
    this.setIsLoggedIn(false);
    this.storageService.removeItem(this.storageKeys.auth, true);
    this.accountService.clearUserAccount();
  }

  /**
   * Formats the provided Auth data (organic or social) into an API payload
   * @param keyword form value (email or phone)
   * @param uuidType login type - only for email or phone
   * @param country selected country - only for phone calling code
   * @param socialUser data from social auth
   * @returns mapped IGuestAuthPayload
   */
  getAuthPayload(
    keyword: string,
    uuidType: ECheckoutUserLoginType,
    country: ICountryPhone,
    socialRedirectUri: string,
    socialUser?: SocialUser | undefined
  ): IGuestAuthPayload {
    if (socialUser) {
      return {
        keyword: this.getSocialAuthKeyword(socialUser),
        type: ECheckoutUserLoginType.social,
        socialProvider: this.getSocialAuthIdentityProvider(socialUser),
        socialCode: socialUser.authorizationCode,
        socialRedirectUri
      };
    }

    if (uuidType === ECheckoutUserLoginType.phone) {
      return { keyword, type: uuidType, callingCode: `+${country.callingCode}` };
    }

    return { keyword, type: uuidType };
  }

  /** Returns the keyword token for the API based on SocialUser provider */
  getSocialAuthKeyword(auth: SocialUser): string {
    switch (auth.provider) {
      case FacebookLoginProvider.PROVIDER_ID:
        return auth.authToken;
      case GoogleLoginProvider.PROVIDER_ID:
      case MicrosoftLoginProvider.PROVIDER_ID:
      case AppleLoginProvider.PROVIDER_ID:
      default:
        return auth.idToken;
    }
  }

  /** Returns the identityProvider Enum for the API based on SocialUser provider */
  getSocialAuthIdentityProvider(auth: SocialUser): ECheckoutUserIdentityProvider {
    switch (auth.provider) {
      case FacebookLoginProvider.PROVIDER_ID:
        return ECheckoutUserIdentityProvider.facebook;
      case MicrosoftLoginProvider.PROVIDER_ID:
        return ECheckoutUserIdentityProvider.microsoft;
      case AppleLoginProvider.PROVIDER_ID:
        return ECheckoutUserIdentityProvider.apple;
      case GoogleLoginProvider.PROVIDER_ID:
      default:
        return ECheckoutUserIdentityProvider.google;
    }
  }

  /** Loads the apple Auth script, ensures this only happens once per app lifecycle */
  loadAppleSignInScript(): void {
    if (this.isAppleSignInLoaded) {
      return;
    }

    this.isAppleSignInLoaded = true;
    this.scriptsService.addScriptElement(this.appleAuthScriptSrc);
  }

  /** Takes the Apple Auth response and maps it to a SocialUser model so we can use similar social auth flows */
  mapAppleAuthToSocialUser(apple: IAppleAuthResponse): SocialUser {
    return {
      provider: AppleLoginProvider.PROVIDER_ID,
      idToken: apple.authorization.id_token,
      firstName: apple.user?.name?.firstName || '',
      lastName: apple.user?.name?.firstName || '',
      email: apple.user?.email || '',
      name: '',
      id: '',
      photoUrl: '',
      authToken: '',
      authorizationCode: apple.authorization.code,
      response: null
    };
  }

  /** Get translate key for error code from an HttpErrorResponse */
  getAuthErrorMessage(errorResponse: HttpErrorResponse, defaultMessage?: string): string {
    const code = this.httpResponseHelperService.getErrorCodes(errorResponse)[0] ?? '';

    switch (code) {
      case 'error_account_locked':
        return 'ACCOUNT.ERROR_ACCOUNT_LOCKED';

      case 'error_invalid_email':
        return 'ACCOUNT.ERROR_INVALID_EMAIL';

      case 'error_email_mobile_simultaneous_update':
        return 'ACCOUNT.ERROR_EMAIL_MOBILE_SIMULTANEOUS_UPDATE';

      case 'error_updated_mobile_already_in_use':
        return 'ACCOUNT.ERROR_UPDATED_MOBILE_ALREADY_IN_USE';

      case 'error_duplicate_mobiles':
        return 'ACCOUNT.ERROR_DUPLICATE_MOBILES';

      default:
        return defaultMessage ?? 'ACCOUNT.ERROR_CREATION';
    }
  }
}
